import React, { useCallback, useEffect, useMemo, useState } from 'react'
import fetchOrderDetails from '../apis/fetchOrderDetails'
import { OrderDetailsScreenProps } from '../routes/types'
import { OrderDetail, OrderResponse } from '../models/orderDetails'
import { ScrollView, Text, View } from 'react-native'
import OrderStatus from '../components/orders/OrderStatus'
import {
  BG_STYLES,
  BORDER_STYLES,
  COMMON_STYLES,
  Spinner,
  TEXT_STYLES,
  TYPOGRAPHY,
  VARIANTS_STYLES,
} from '@dehaat/dds'
import getDate from '../utils/helper'
import OrderProducts from '../components/orders/OrderProducts'
import OrderTimeline from '../components/orders/OrderTimeLine'
import { ORDER_TYPE } from '../constants/order'
import BillDetails from '../components/orders/BillDetails'
import ShipingDetail from '../components/orders/ShipingDetail'
import DcCard from '../components/orders/DcCard'

const OrderDetails = ({ route }: OrderDetailsScreenProps) => {
  const [orderDetails, setOrderDetails] = useState<OrderResponse>()
  const [loading, setLoading] = useState(false)
  const { orderId } = route.params

  const getOrderDetails = useCallback(async () => {
    setLoading(true)
    const response = await fetchOrderDetails(orderId)
    if (response) {
      setOrderDetails(response)
    }
    setLoading(false)
  }, [orderId])

  const getTotalInsuranceAmount = (orderList: OrderDetail[]) => {
    return orderList.reduce((sum, { insurance }) => {
      if (insurance.length > 0) {
        const { units_purchased, cost_to_farmer } = insurance[0]
        return sum + units_purchased * Number(cost_to_farmer)
      }
      return sum
    }, 0)
  }

  const insuranceCharges = useMemo(
    () =>
      orderDetails?.bundled_insurance
        ? getTotalInsuranceAmount(orderDetails?.order_details || [])
        : undefined,
    [orderDetails?.order_details, orderDetails?.bundled_insurance],
  )

  const handleCancelOrder = (order: OrderResponse) => {
    setOrderDetails(order)
  }

  useEffect(() => {
    getOrderDetails()
  }, [getOrderDetails])

  if (loading) {
    return <Spinner />
  }

  if (orderDetails) {
    let itemTotal = Number(orderDetails.price)
    const isPickupFromStore = orderDetails.type === ORDER_TYPE.SELF_PICKUP

    if (insuranceCharges) {
      itemTotal -= insuranceCharges
    }

    const vendorDetails = orderDetails.seller_details
    const { company_name, company_address } = vendorDetails || {}

    return (
      <ScrollView>
        <View style={{ gap: 8 }}>
          <OrderStatus
            deliveryDate={getDate(orderDetails?.delivered_on!, true, false)}
            status={orderDetails?.status!}
            orderType={orderDetails?.type!}
            onlinePaymentStatus={orderDetails?.payment_method!}
          />
          <View
            style={[COMMON_STYLES.ph2, COMMON_STYLES.pv1, BG_STYLES.bgWhite]}
          >
            <Text
              style={[
                TYPOGRAPHY.HEADING3,
                VARIANTS_STYLES.EMPHASIZED,
                TEXT_STYLES.textNeutral90,
              ]}
            >
              {`Order Id:${orderDetails?.id}`}
            </Text>
            <View
              style={[
                COMMON_STYLES.flex1,
                COMMON_STYLES.row,
                { columnGap: 16, borderBottomWidth: 1, paddingBottom: 12 },
                COMMON_STYLES['mt1/2'],
                BORDER_STYLES.borderNeutral20,
              ]}
            >
              <Text
                style={[
                  TYPOGRAPHY.TEXT2,
                  VARIANTS_STYLES.MEDIUM,
                  TEXT_STYLES.textNeutral80,
                ]}
              >
                {getDate(orderDetails?.delivered_on!, false, true)}
              </Text>
              <Text
                style={[
                  TYPOGRAPHY.TEXT2,
                  VARIANTS_STYLES.MEDIUM,
                  TEXT_STYLES.textNeutral80,
                ]}
              >
                {orderDetails?.buyer_details.name}
              </Text>
              <Text
                style={[
                  TYPOGRAPHY.TEXT2,
                  VARIANTS_STYLES.MEDIUM,
                  TEXT_STYLES.textNeutral80,
                ]}
              >
                {orderDetails?.order_details.length}
              </Text>
            </View>
            <View style={[COMMON_STYLES['mt1/2']]}>
              <View style={[COMMON_STYLES.row, COMMON_STYLES.justifyBetween]}>
                <Text
                  style={[
                    TYPOGRAPHY.TEXT2,
                    VARIANTS_STYLES.BASE,
                    TEXT_STYLES.textNeutral80,
                  ]}
                >
                  Payable Amount
                </Text>
                <Text
                  style={[
                    TYPOGRAPHY.TEXT1,
                    VARIANTS_STYLES.MEDIUM,
                    TEXT_STYLES.textNeutral90,
                  ]}
                >{`₹${orderDetails?.grand_total}`}</Text>
              </View>
              <View
                style={[
                  COMMON_STYLES['mt3/4'],
                  COMMON_STYLES.row,
                  COMMON_STYLES.justifyBetween,
                ]}
              >
                <Text
                  style={[
                    TYPOGRAPHY.TEXT2,
                    VARIANTS_STYLES.BASE,
                    TEXT_STYLES.textSuccess100,
                  ]}
                >
                  Paid Amount
                </Text>
                <Text
                  style={[
                    TYPOGRAPHY.TEXT1,
                    VARIANTS_STYLES.MEDIUM,
                    TEXT_STYLES.textSuccess100,
                  ]}
                >
                  {orderDetails?.paid_status}
                </Text>
              </View>
            </View>
          </View>
          <View
            style={[COMMON_STYLES.ph2, COMMON_STYLES.pv1, BG_STYLES.bgWhite]}
          >
            <OrderProducts productList={orderDetails?.order_details!} />
            <OrderTimeline
              orderId={orderDetails?.id.toString()!}
              orderDate={orderDetails?.order_date!}
              deliveryDate={orderDetails?.delivered_on!}
              status={orderDetails?.status!}
              isPickupFromStore={isPickupFromStore}
              onCancelOrder={handleCancelOrder}
            />
          </View>
          <View>
            <BillDetails
              totalAmount={itemTotal}
              totalDiscount={Number(orderDetails.discount)}
              shippingCharges={Number(orderDetails.delivery_fee)}
              couponAmount={
                orderDetails.coupon?.discount
                  ? Number(orderDetails.coupon.discount)
                  : 0
              }
              couponCode={orderDetails.coupon?.code}
              insuranceCharges={insuranceCharges}
            />
          </View>
          <View>
            <ShipingDetail
              orderId={orderDetails.id.toString()}
              orderDate={getDate(orderDetails.order_date, false, true)!}
              address={orderDetails.full_display_address}
              paymentMethod={orderDetails.payment_method}
            />
          </View>

          {vendorDetails && !isPickupFromStore ? (
            <View
              style={[COMMON_STYLES.ph2, COMMON_STYLES.pv1, BG_STYLES.bgWhite]}
            >
              <Text
                style={[
                  TYPOGRAPHY.HEADING3,
                  VARIANTS_STYLES.EMPHASIZED,
                  TEXT_STYLES.textNeutral90,
                ]}
              >
                Seller Details
              </Text>
              <DcCard name={company_name} address={company_address} />
            </View>
          ) : null}
        </View>
      </ScrollView>
    )
  }
}

export default OrderDetails
